import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useI18n } from "./i18n";

const TAHAR_IMAGE = "https://dream-five.com/wp-content/uploads/2025/01/tahar-poster-768x1075.jpg";

export default function HomepageHallOfFameInjector() {
  const { language } = useI18n();
  const [mountNode, setMountNode] = useState(null);

  useEffect(() => {
    const anchor = document.querySelector("#coaches");
    if (!anchor?.parentNode) return undefined;

    let node = document.getElementById("homepage-hall-of-fame-root");
    const created = !node;
    if (!node) {
      node = document.createElement("div");
      node.id = "homepage-hall-of-fame-root";
      anchor.parentNode.insertBefore(node, anchor.nextSibling);
    }
    setMountNode(node);

    return () => {
      setMountNode(null);
      if (created && node?.parentNode) node.parentNode.removeChild(node);
    };
  }, []);

  useEffect(() => {
    const nav = document.querySelector(".nav");
    const teamLink = nav?.querySelector('a[href="#coaches"]');
    if (!nav || !teamLink) return undefined;

    let link = nav.querySelector("#hall-of-fame-nav-link");
    if (!link) {
      link = document.createElement("a");
      link.id = "hall-of-fame-nav-link";
      teamLink.insertAdjacentElement("afterend", link);
    }
    link.textContent = "Hall of Fame";
    link.href = "#hall-of-fame";

    return () => link?.remove();
  }, [language]);

  if (!mountNode) return null;

  const isDe = language === "de";
  const path = `/${language}/hall-of-fame/`;
  const copy = isDe ? {
    eyebrow: "COMPETITION TEAM",
    title: "Hall of Fame",
    intro: "Seit 2023 hat sich Tiago De Paula BJJ zu einem international sichtbaren Competition Team entwickelt – mit Erfolgen auf IBJJF-, ADCC-, AJP- und weiteren Matten.",
    hero: "Von Zürich auf internationale Podien.",
    heroSub: "Kids · Adults · Masters · Pro",
    medals: "IBJJF Medaillen seit 2023",
    athletes: "Athlet:innen",
    gold: "Gold",
    silver: "Silber",
    bronze: "Bronze",
    from: "von 17 verschiedenen Wettkämpfer:innen",
    circuit: "IBJJF-Circuit",
    featured: "FEATURED ATHLETE",
    taharCopy: "Die stärkste international belegbare Titelgeschichte aus dem Team: Major-Titel in Europa und Las Vegas sowie ein dokumentierter AJP-World-Titel in Abu Dhabi.",
    full: "Alle Erfolge ansehen",
    baseline: "IBJJF ist nur die Baseline.",
    additional: "ADCC, AJP, FIJJD und Schweizer Resultate kommen zusätzlich hinzu.",
    emily: "Referee · ADCC Amateur Worlds · Kraków 2026",
  } : {
    eyebrow: "COMPETITION TEAM",
    title: "Hall of Fame",
    intro: "Since 2023, Tiago De Paula BJJ has developed into an internationally visible competition team, with results across IBJJF, ADCC, AJP and other events.",
    hero: "From Zurich to international podiums.",
    heroSub: "Kids · Adults · Masters · Pro",
    medals: "IBJJF medals since 2023",
    athletes: "Athletes",
    gold: "Gold",
    silver: "Silver",
    bronze: "Bronze",
    from: "from 17 different competitors",
    circuit: "IBJJF circuit",
    featured: "FEATURED ATHLETE",
    taharCopy: "The team’s strongest internationally documented title story: major titles in Europe and Las Vegas, plus a documented AJP world title in Abu Dhabi.",
    full: "View all achievements",
    baseline: "IBJJF is only the baseline.",
    additional: "ADCC, AJP, FIJJD and Swiss results come on top of that.",
    emily: "Referee · ADCC Amateur Worlds · Kraków 2026",
  };

  return createPortal(
    <section className="hof-home" id="hall-of-fame" aria-labelledby="hall-of-fame-title">
      <div className="section-shell hof-home__head">
        <div>
          <p className="eyebrow">{copy.eyebrow}</p>
          <h2 id="hall-of-fame-title">{copy.title}</h2>
          <div className="hof-rule" />
        </div>
        <p className="hof-home__intro">{copy.intro}</p>
      </div>

      <div className="hof-home__hero">
        <img src="/images/emilian-gold-barcelona.svg" alt="Tiago de Paula BJJ competition" loading="lazy" />
        <div className="section-shell hof-home__hero-copy">
          <strong>{copy.hero}</strong>
          <span>{copy.heroSub}</span>
        </div>
      </div>

      <div className="section-shell hof-stats" aria-label="IBJJF competition statistics">
        <div className="hof-stat hof-stat--lead"><strong>37</strong><b>{copy.medals}</b><span>{copy.from}</span></div>
        <div className="hof-stat"><strong>17</strong><b>{copy.athletes}</b><span>{copy.circuit}</span></div>
        <div className="hof-stat"><strong className="gold">14</strong><b>{copy.gold}</b><span>IBJJF</span></div>
        <div className="hof-stat"><strong className="silver">8</strong><b>{copy.silver}</b><span>IBJJF</span></div>
        <div className="hof-stat"><strong className="bronze">15</strong><b>{copy.bronze}</b><span>IBJJF</span></div>
      </div>

      <div className="section-shell hof-feature">
        <div className="hof-feature__photo">
          <img src={TAHAR_IMAGE} referrerPolicy="no-referrer" alt="Tahar Semlali" loading="lazy" />
        </div>
        <div className="hof-feature__copy">
          <p className="eyebrow">{copy.featured}</p>
          <h3>Tahar Semlali</h3>
          <div className="hof-feature__sub">IBJJF World Master Champion · IBJJF European Champion · AJP World Champion</div>
          <p>{copy.taharCopy}</p>
          <div className="hof-major">
            <div><b>World Master IBJJF 2024</b><span>Gold · Master 2 Blue · Rooster</span></div>
            <div><b>European IBJJF 2024</b><span>Gold · Master 1 White · Light-Feather</span></div>
            <div><b>AJP World Abu Dhabi 2024</b><span>Gold · athlete/academy documented</span></div>
            <div><b>International Major Results</b><span>Europe · USA · Abu Dhabi</span></div>
          </div>
          <a className="hof-link" href={path}>{copy.full} →</a>
        </div>
      </div>

      <div className="section-shell hof-examples">
        <article className="hof-example"><small>Masters</small><h3>Elisabetta<br />Franceschin</h3><p>European No-Gi Champion + Open-Class Champion 2024.</p><div className="hof-example__result">2× Gold · 2× Silver</div></article>
        <article className="hof-example"><small>Masters</small><h3>Pablo<br />Koerfer</h3><p>Geneva Gi / No-Gi Gold, Open-Class Silver, European No-Gi Bronze.</p><div className="hof-example__result">2× Gold · 1× Silver · 1× Bronze</div></article>
        <article className="hof-example"><small>Kids & Youth</small><h3>Ana Julia<br />Silva Muniz</h3><p>IBJJF European Kids 2025.</p><div className="hof-example__result">European Silver</div></article>
        <article className="hof-example"><small>Kids</small><h3>Emilian<br />Bello</h3><p>ADCC National, ADCC Open, Zurich Spring Open, FIJJD Barcelona.</p><div className="hof-example__result">4 documented Gold results</div></article>
        <article className="hof-example hof-example--emily">
          <img src="/images/emily-adcc-referee.svg" alt="Emily, ADCC referee" loading="lazy" />
          <div className="hof-example__copy"><small>Beyond the podium</small><h3>Emily</h3><p>{copy.emily}</p></div>
        </article>
      </div>

      <div className="section-shell hof-home__cta">
        <p><strong>{copy.baseline}</strong> {copy.additional}</p>
        <a className="hof-btn" href={path}>{copy.full} →</a>
      </div>
    </section>,
    mountNode,
  );
}
